import { supabase } from '../../shared/supabase/supabaseClient';
import type { GlobalFeatureFlagsMap } from './domain/globalFeatureFlags';
import { debugServiceFetchWarn } from '../../shared/dev/debugServiceFetches';
import { recordStartupNetworkCall } from '../../shared/dev/networkCallAudit';
import { GLOBAL_FEATURE_FLAGS_CACHE_MS } from '../session/sessionCacheTtl';

export { GLOBAL_FEATURE_FLAGS_CACHE_MS };

let globalFeatureFlagsCache: { value: GlobalFeatureFlagsMap | null; fetchedAt: number } | null = null;
let globalFeatureFlagsInFlight: Promise<GlobalFeatureFlagsMap | null> | null = null;

export function primeGlobalFeatureFlagsCache(flags: GlobalFeatureFlagsMap | null): void {
  globalFeatureFlagsCache = { value: flags, fetchedAt: Date.now() };
}

export function getGlobalFeatureFlagsCacheIfFresh(
  now = Date.now()
): GlobalFeatureFlagsMap | null | undefined {
  if (!globalFeatureFlagsCache || now - globalFeatureFlagsCache.fetchedAt >= GLOBAL_FEATURE_FLAGS_CACHE_MS) {
    return undefined;
  }
  return globalFeatureFlagsCache.value;
}

export function isGlobalFeatureFlagsCacheFresh(now = Date.now()): boolean {
  return globalFeatureFlagsCache != null && now - globalFeatureFlagsCache.fetchedAt < GLOBAL_FEATURE_FLAGS_CACHE_MS;
}

export function setGlobalFeatureFlagsCache(flags: GlobalFeatureFlagsMap | null): void {
  globalFeatureFlagsCache = { value: flags, fetchedAt: Date.now() };
}

export function clearGlobalFeatureFlagsCache(): void {
  globalFeatureFlagsCache = null;
}

export function getCachedGlobalFeatureFlagsForSync(): GlobalFeatureFlagsMap | null | undefined {
  return getGlobalFeatureFlagsCacheIfFresh();
}

export const GLOBAL_FEATURE_KEYS = {
  mercadopago_integrations: 'mercadopago_integrations',
  arca_integrations: 'arca_integrations',
  fiado_integrations: 'fiado_integrations',
  cash_outflows: 'cash_outflows',
  promotions_integrations: 'promotions_integrations',
  price_lists_integrations: 'price_lists_integrations',
  loyalty_integrations: 'loyalty_integrations',
  scale_integration: 'scale_integration',
  public_store_features: 'public_store_features',
  billing_enabled: 'billing_enabled',
  web_online_writes: 'web_online_writes',
} as const;

export async function fetchGlobalFeatureFlags(options?: {
  force?: boolean;
}): Promise<GlobalFeatureFlagsMap | null> {
  const stack = new Error().stack?.split('\n').slice(2, 5).join(' | ') ?? 'unknown';
  debugServiceFetchWarn('[globalFeatureFlags] fetchGlobalFeatureFlags llamado desde:', stack);
  const now = Date.now();
  if (!options?.force) {
    const cached = getGlobalFeatureFlagsCacheIfFresh(now);
    if (cached !== undefined) {
      debugServiceFetchWarn('[globalFeatureFlags] retornando cache');
      return cached;
    }
  }
  if (globalFeatureFlagsInFlight) {
    debugServiceFetchWarn('[globalFeatureFlags] coalesciendo con in-flight');
    return globalFeatureFlagsInFlight;
  }

  globalFeatureFlagsInFlight = fetchGlobalFeatureFlagsRemote().finally(() => {
    globalFeatureFlagsInFlight = null;
  });
  return globalFeatureFlagsInFlight;
}

async function fetchGlobalFeatureFlagsRemote(): Promise<GlobalFeatureFlagsMap | null> {
  recordStartupNetworkCall('feature_flags', 'get_global_feature_flags');
  const { data, error } = await supabase.rpc('get_global_feature_flags');
  if (error) {
    console.warn('[globalFeatureFlags]', error.message);
    // Un fetch fallido no es un cache válido: si lo cacheamos como null,
    // los resolvers fail-closed (saleOnlinePath, deviceCommandInbox, etc.)
    // tratan null como "flags cargados, todos off" y no refetchean por 24h.
    // Limpiar el cache para que la próxima lectura haga fetch nuevo.
    clearGlobalFeatureFlagsCache();
    return null;
  }
  const flags = data == null || typeof data !== 'object' ? null : (data as GlobalFeatureFlagsMap);
  setGlobalFeatureFlagsCache(flags);
  return flags;
}

/**
 * Legacy opt-out gate: flags existentes permanecen habilitados cuando aún no
 * llegaron, salvo que el backend los desactive explícitamente.
 */
export function isGlobalFeatureOn(flags: GlobalFeatureFlagsMap | null | undefined, key: keyof GlobalFeatureFlagsMap): boolean {
  if (!flags) return true;
  const v = flags[key];
  return v !== false;
}

/** AI is opt-in and must fail closed if its flag cannot be resolved. */
export function isAiChatFeatureOn(flags: GlobalFeatureFlagsMap | null | undefined): boolean {
  return flags?.ai_chat_enabled === true;
}
